import { X } from "lucide-react";

export const inputCls =
  "w-full rounded-lg border border-navy-900/15 bg-white px-3.5 py-2.5 text-sm text-navy-900 placeholder:text-slate-400 focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/25";

export const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500";

export const hasPerm = (emp, perm) => {
  const p = emp?.perms || [];
  return p.includes("*") || p.includes(perm);
};

export const fmtDate = (iso) =>
  iso
    ? new Date(iso.length === 10 ? `${iso}T00:00:00` : iso).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

export const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

export const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_STYLES = {
  Pending: "bg-amber-100 text-amber-800 border-amber-200",
  "Manager Approved": "bg-sky-100 text-sky-800 border-sky-200",
  Approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Rejected: "bg-rose-100 text-rose-700 border-rose-200",
  Cancelled: "bg-slate-100 text-slate-500 border-slate-200",
  Present: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Work From Home": "bg-sky-100 text-sky-800 border-sky-200",
  Leave: "bg-violet-100 text-violet-800 border-violet-200",
  Holiday: "bg-gold/15 text-gold-dark border-gold/30",
  "Weekly Off": "bg-slate-100 text-slate-500 border-slate-200",
  "Not Checked In": "bg-slate-100 text-slate-600 border-slate-200",
  Draft: "bg-slate-100 text-slate-600 border-slate-200",
  "Under Review": "bg-amber-100 text-amber-800 border-amber-200",
  Submitted: "bg-sky-100 text-sky-800 border-sky-200",
  Processing: "bg-sky-100 text-sky-800 border-sky-200",
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "On Hold": "bg-orange-100 text-orange-800 border-orange-200",
  Failed: "bg-rose-100 text-rose-700 border-rose-200",
  Active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Inactive: "bg-slate-100 text-slate-500 border-slate-200",
  Probation: "bg-sky-100 text-sky-800 border-sky-200",
  Internship: "bg-violet-100 text-violet-800 border-violet-200",
  "Notice Period": "bg-orange-100 text-orange-800 border-orange-200",
  "On Leave": "bg-violet-100 text-violet-800 border-violet-200",
};

export function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span
      data-testid={`status-${String(status).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${cls}`}
    >
      {status}
    </span>
  );
}

export function PageHeader({ title, sub, children }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-serif text-3xl text-navy-900 sm:text-4xl">{title}</h1>
        {sub && <p className="mt-1.5 text-sm text-slate-500">{sub}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-3">{children}</div>}
    </div>
  );
}

export function Card({ className = "", testid, children }) {
  return (
    <div
      data-testid={testid}
      className={`rounded-2xl border border-navy-900/10 bg-white p-6 shadow-[0_1px_3px_rgba(11,19,43,0.05)] ${className}`}
    >
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, sub, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-navy-900/15 bg-white/60 px-6 py-14 text-center"
    >
      {Icon && <Icon className="h-8 w-8 text-gold-dark" aria-hidden />}
      <p className="mt-4 font-serif text-lg text-navy-900">{title}</p>
      {sub && <p className="mt-1 max-w-sm text-sm text-slate-500">{sub}</p>}
    </div>
  );
}

export function Spinner() {
  return (
    <div className="flex items-center justify-center py-20" data-testid="loading-spinner">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-gold border-t-transparent" aria-label="Loading" />
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-navy-950/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className={`relative max-h-[90vh] w-full ${wide ? "max-w-3xl" : "max-w-lg"} overflow-y-auto rounded-2xl border border-navy-900/10 bg-white p-6 shadow-2xl sm:p-8`}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <h3 className="font-serif text-xl text-navy-900">{title}</h3>
          <button
            onClick={onClose}
            data-testid="modal-close"
            aria-label="Close"
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy-900"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}
